import React from "react";
import type { FlipperProps, FlipperState, InProgressAnimations, FlipCallbacks } from "./types";
import type { FlippedElementPositionsBeforeUpdateReturnType } from "./flip/getFlippedElementPositions/getFlippedElementPositionsBeforeUpdate/types";
import { getFlippedElementPositionsBeforeUpdate } from "./flip/getFlippedElementPositions/getFlippedElementPositionsBeforeUpdate";
import { FlipContext } from "./context";
import { onFlipIdsUpdate } from "./flip";

/**
 * Thằng này sẽ chứa internal animation engine.
 * Ở mỗi phase, mỗi callback, chúng sẽ được truyền thêm internal animation engine này vào để thực hiện animation.
 * animation engine sẽ expose ra các api như thực hiện animation, sync với ScrollAnimationTimeLine hay ViewTimeLine của chat box list hay các Timeline khác.
 */

export class Flipper extends React.Component<
	FlipperProps,
	FlipperState,
	FlippedElementPositionsBeforeUpdateReturnType
> {
	static defaultProps = {
		applyTransformOrigin: true,
		element: "div",
	};

	private inProgressAnimations: InProgressAnimations = {};
	private flipCallbacks: FlipCallbacks = {};
	private el?: HTMLElement = undefined;

	constructor(props: FlipperProps) {
		super(props);
		this.markDebugLifecycleIfEnabled("debugCtor");

		this.state = {
			shouldFlipOnMount: false,
		};
	}

	markDebugLifecycleIfEnabled(name: string) {
		//@ts-ignore
		if (Boolean(this.props.advancedDebugOpts?.reactLifecycle?.[name])) debugger;
	}

	render() {
		this.markDebugLifecycleIfEnabled("debugRender");

		const {
			flipIds: _flipIds,
			shouldFlipOnMount: _shouldFlipOnMount,
			applyTransformOrigin: _applyTransformOrigin,
			debug: _debug,
			advancedDebugOpts: _advancedDebugOpts,
			element: _element,
			decisionData: _decisionData,
			onComplete: _onComplete,
			onStart: _onStart,
			animationNG: _animationNG,
			...restProps
		} = this.props;
		const Element = _element as React.ElementType;

		return (
			<FlipContext.Provider value={this.flipCallbacks}>
				<Element ref={(el: HTMLElement) => (this.el = el)} {...restProps} />
			</FlipContext.Provider>
		);
	}

	componentDidMount(): void {
		this.markDebugLifecycleIfEnabled("debugDidMount");

		const { shouldFlipOnMount = false } = this.props;
		if (shouldFlipOnMount) {
			this.setState({
				shouldFlipOnMount: true,
			});
		}
	}

	getSnapshotBeforeUpdate(
		prevProps: Readonly<FlipperProps>,
		prevState: Readonly<FlipperState>,
	): FlippedElementPositionsBeforeUpdateReturnType | null {
		this.markDebugLifecycleIfEnabled("debugGetSS");

		if (prevProps.flipIds !== this.props.flipIds && this.el) {
			return getFlippedElementPositionsBeforeUpdate({
				element: this.el,
				/** If onExit callbacks exist here, we'll cache the DOM node of the elements that are going to exit. */
				flipCallbacks: this.flipCallbacks,
				inProgressAnimations: this.inProgressAnimations,
			});
		}
		return null;
	}

	componentDidUpdate(
		prevProps: Readonly<FlipperProps>,
		prevState: Readonly<FlipperState>,
		snapshot?: FlippedElementPositionsBeforeUpdateReturnType,
	): void {
		this.markDebugLifecycleIfEnabled("debugDidUpdate");

		if (prevProps.flipIds !== this.props.flipIds && this.el && snapshot) {
			onFlipIdsUpdate({
				flippedElementPositionsBeforeUpdate: snapshot.flippedElementPositions,
				cachedOrderedFlipIds: snapshot.cachedOrderedFlipIds,
				containerEl: this.el,
				inProgressAnimations: this.inProgressAnimations,
				flipCallbacks: this.flipCallbacks,
				applyTransformOrigin: this.props.applyTransformOrigin,
				animationNG: this.props.animationNG,
				// spring: this.props.spring,
				debug: this.props.debug,
				// portalKey: this.props.portalKey,
				// staggerConfig: this.props.staggerConfig,
				// handleEnterUpdateDelete: this.props.handleEnterUpdateDelete,
				decisionData: {
					previous: prevProps.decisionData,
					current: this.props.decisionData,
				},
				onComplete: this.props.onComplete,
				onStart: this.props.onStart,
			});
		}
	}

	componentWillUnmount(): void {
		this.markDebugLifecycleIfEnabled("debugWillUnmount");
	}
}
